import { Signal } from "@engine";
import type { Player } from "../player/Player";
import type { PlayerTrail } from "../fx/PlayerTrail";
import type { AchievementService } from "./AchievementService";
import {
  DEFAULT_SKIN_ID,
  SKINS,
  skinById,
  type SkinDef,
} from "../cosmetics/skins";
import {
  DEFAULT_TRAIL_ID,
  TRAIL_PALETTES,
  trailById,
  type TrailPaletteDef,
} from "../cosmetics/trails";

const STORAGE_KEY = "morph.cosmetics.v1";

interface PersistedSelection {
  skinId: string;
  trailId: string;
}

// Owns the player's cosmetic choices. Listens to AchievementService to
// know what's unlocked, and exposes a single apply(player, trail) call
// that the game runs at startGame time so the visuals always match the
// player's selection on the most recent run.
//
// We deliberately keep the "currently selected" state separate from
// "currently applied" -- so the StatsScreen can preview a skin/trail
// before the next run starts (callers re-call apply() after toggling
// selection if they want immediate feedback).
export class CosmeticsService {
  readonly onSelectionChanged = new Signal<PersistedSelection>();

  private achievements: AchievementService;
  private selection: PersistedSelection = {
    skinId: DEFAULT_SKIN_ID,
    trailId: DEFAULT_TRAIL_ID,
  };

  constructor(achievements: AchievementService) {
    this.achievements = achievements;
    this.load();
  }

  // Skins / trails are lazily filtered at read time so newly-unlocked
  // items show up immediately after their unlock without us having to
  // re-emit anything.
  getAvailableSkins(): SkinDef[] {
    return SKINS.filter((s) => this.skinUnlocked(s.id));
  }

  getAvailableTrails(): TrailPaletteDef[] {
    return TRAIL_PALETTES.filter((t) => this.trailUnlocked(t.id));
  }

  // Full catalogues with locked entries present (StatsScreen renders
  // them greyed out so the player can see what's coming).
  getAllSkins(): SkinDef[] {
    return SKINS;
  }

  getAllTrails(): TrailPaletteDef[] {
    return TRAIL_PALETTES;
  }

  skinUnlocked(id: string): boolean {
    const def = skinById(id);
    return def.requires === null || this.achievements.isUnlocked(def.requires);
  }

  trailUnlocked(id: string): boolean {
    const def = trailById(id);
    return def.requires === null || this.achievements.isUnlocked(def.requires);
  }

  getSelectedSkin(): SkinDef {
    return skinById(this.selection.skinId);
  }

  getSelectedTrail(): TrailPaletteDef {
    return trailById(this.selection.trailId);
  }

  // Returns the new selection (for chaining). No-op if the requested
  // skin isn't unlocked yet -- callers can avoid this by hiding locked
  // entries from the UI in the first place.
  selectSkin(id: string): PersistedSelection {
    if (!this.skinUnlocked(id)) return this.selection;
    if (this.selection.skinId === id) return this.selection;
    this.selection = { ...this.selection, skinId: id };
    this.persist();
    this.onSelectionChanged.emit(this.selection);
    return this.selection;
  }

  selectTrail(id: string): PersistedSelection {
    if (!this.trailUnlocked(id)) return this.selection;
    if (this.selection.trailId === id) return this.selection;
    this.selection = { ...this.selection, trailId: id };
    this.persist();
    this.onSelectionChanged.emit(this.selection);
    return this.selection;
  }

  // Push the current selection onto the player's material + the trail's
  // tier color list. Cheap to call frequently (mutates in place); used
  // both at startGame() and when the StatsScreen wants live preview.
  apply(player: Player, trail: PlayerTrail): void {
    const skin = this.getSelectedSkin();
    player.setMaterialConfig({
      color: skin.color,
      emissive: skin.emissive,
      emissiveIntensity: skin.emissiveIntensity,
      roughness: skin.roughness,
      metalness: skin.metalness,
    });
    const palette = this.getSelectedTrail();
    trail.setTierColors(palette.colors);
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<PersistedSelection> | null;
      if (!parsed) return;
      // Defensive: if a previously-selected cosmetic disappears (we
      // renamed/removed an id) fall back to defaults rather than leave
      // the player on a broken selection.
      const skinId =
        typeof parsed.skinId === "string" && SKINS.some((s) => s.id === parsed.skinId)
          ? parsed.skinId
          : DEFAULT_SKIN_ID;
      const trailId =
        typeof parsed.trailId === "string" &&
        TRAIL_PALETTES.some((t) => t.id === parsed.trailId)
          ? parsed.trailId
          : DEFAULT_TRAIL_ID;
      this.selection = { skinId, trailId };
    } catch {
      // ignore
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.selection));
    } catch {
      // ignore
    }
  }
}
